import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Compartment, EditorState, Extension } from '@codemirror/state';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  drawSelection,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentSelection,
} from '@codemirror/commands';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import {
  bracketMatching,
  indentUnit,
  syntaxHighlighting,
  defaultHighlightStyle,
} from '@codemirror/language';
import { cpp } from '@codemirror/lang-cpp';
import { python } from '@codemirror/lang-python';
import { java } from '@codemirror/lang-java';
import { sql } from '@codemirror/lang-sql';
import { javascript } from '@codemirror/lang-javascript';
import { monokai } from '@uiw/codemirror-theme-monokai';

/**
 * CodeMirror 6 wrapper, drop-in replacement for <ngx-codemirror>.
 *
 * Supports [(ngModel)] via ControlValueAccessor and accepts the same
 * conceptual inputs the app already uses (language, readOnly, theme,
 * paste-blocking, "n." line-number format, custom Enter behaviour).
 *
 * `language` accepts either a simple key ('cpp' | 'c' | 'csharp' | 'java' |
 * 'python' | 'sql' | 'javascript') or the existing CM5 MIME mode strings
 * ('text/x-c++src', 'text/x-java', 'python', 'sql', ...) so call sites can be
 * migrated without rewriting their language mapping.
 */
@Component({
  selector: 'app-code-editor',
  standalone: true,
  template: `<div #host class="cm6-host"></div>`,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      .cm6-host,
      .cm6-host .cm-editor {
        height: 100%;
      }
      .cm6-host .cm-scroller {
        overflow: auto;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeEditorComponent),
      multi: true,
    },
  ],
})
export class CodeEditorComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;

  @Input() language = 'javascript';
  @Input() readOnly = false;
  /** Block paste into the editor (anti-cheat parity with CM5 onPaste). */
  @Input() blockPaste = false;
  /** Auto-close brackets/quotes while typing (CM5 autoCloseBrackets). */
  @Input() autoCloseBrackets = false;
  /**
   * Re-indent the whole document with the language's grammar when content is
   * set externally (e.g. backend boilerplate arrives flat). Skipped for Python
   * (significant whitespace) and read-only editors.
   */
  @Input() formatOnLoad = false;
  /** Render line numbers as "1.", "2." like the CM5 lineNumberFormatter. */
  @Input() numberedLines = true;
  @Input() theme: 'monokai' | 'default' = 'monokai';

  /**
   * Backward-compat: accept the existing CM5 options object (the same
   * `editorOptions` the app already passes to <ngx-codemirror>). When present,
   * its fields drive the editor so call sites migrate with a pure tag rename.
   * Explicit @Inputs above are used only when `options` is not provided.
   */
  @Input() options: any = null;

  /** Fires on every document change (parity with (ngModelChange)). */
  @Output() codeChange = new EventEmitter<string>();
  /**
   * Fires when Enter is pressed. Emits a CM5-compatible shim exposing
   * `lineCount()` and `getLine(i)` so existing handlers (e.g. onEnterPress)
   * work unchanged against the CM6 document.
   */
  @Output() enterPressed = new EventEmitter<any>();
  /** Fires when a paste was blocked (so the host can show a toast). */
  @Output() pasteBlocked = new EventEmitter<void>();

  private view?: EditorView;
  private value = '';
  private formatHandle: any = null;

  // Compartments let us reconfigure individual aspects without rebuilding state.
  private languageConf = new Compartment();
  private readOnlyConf = new Compartment();
  private themeConf = new Compartment();

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.view) {
      return;
    }
    // Any of these can change the language / readonly / theme, so reconfigure
    // the relevant compartments. `options` can affect all three.
    if (changes['language'] || changes['options']) {
      this.view.dispatch({
        effects: this.languageConf.reconfigure(this.languageExtension()),
      });
    }
    if (changes['readOnly'] || changes['options']) {
      this.view.dispatch({
        effects: this.readOnlyConf.reconfigure(this.readOnlyExtension()),
      });
    }
    if (changes['theme'] || changes['options']) {
      this.view.dispatch({
        effects: this.themeConf.reconfigure(this.themeExtension()),
      });
    }
  }

  // ----- effective config (explicit @Inputs, or the CM5 options object) -----
  private effectiveLanguage(): string {
    if (this.options && this.options.mode) {
      // CM5 mode can be a string ('python') or an object ({ name: 'sql' }).
      return typeof this.options.mode === 'string'
        ? this.options.mode
        : this.options.mode?.name || 'javascript';
    }
    return this.language;
  }

  private effectiveReadOnly(): boolean {
    return this.options ? !!this.options.readOnly : this.readOnly;
  }

  private effectiveTheme(): 'monokai' | 'default' {
    if (this.options) {
      return this.options.theme === 'monokai' ? 'monokai' : 'default';
    }
    return this.theme;
  }

  private effectiveShowLineNumbers(): boolean {
    // CM5 `lineNumbers: false` hides the gutter entirely (output panel).
    return this.options ? this.options.lineNumbers !== false : true;
  }

  private effectiveNumberedLines(): boolean {
    return this.options
      ? typeof this.options.lineNumberFormatter === 'function'
      : this.numberedLines;
  }

  private effectiveBlockPaste(): boolean {
    return this.options ? typeof this.options.onPaste === 'function' : this.blockPaste;
  }

  private effectiveAutoCloseBrackets(): boolean {
    return this.options ? !!this.options.autoCloseBrackets : this.autoCloseBrackets;
  }

  ngOnDestroy(): void {
    if (this.formatHandle != null) {
      clearTimeout(this.formatHandle);
    }
    this.view?.destroy();
  }

  // ----- ControlValueAccessor (so [(ngModel)] works unchanged) -----
  writeValue(value: string): void {
    this.value = value ?? '';
    if (!this.view) {
      // First write happens before the view is created in ngAfterViewInit-like
      // timing; create it lazily here so static ViewChild is ready.
      this.createView();
      this.maybeScheduleFormat();
      return;
    }
    const current = this.view.state.doc.toString();
    if (current !== this.value) {
      this.view.dispatch({
        changes: { from: 0, to: current.length, insert: this.value },
      });
      this.maybeScheduleFormat();
    }
  }

  // ----- auto-format on external content load (e.g. flat boilerplate) -----
  private maybeScheduleFormat(): void {
    if (this.formatOnLoad && this.value && this.value.trim()) {
      // Defer so any concurrent language/options change settles first.
      if (this.formatHandle != null) {
        clearTimeout(this.formatHandle);
      }
      this.formatHandle = setTimeout(() => this.formatCode(), 0);
    }
  }

  /**
   * Re-indent the whole document with the language's grammar. Public so a host
   * can trigger formatting on demand at any time (e.g. a "Format Code" button),
   * not just on load. No-op for Python (significant whitespace) and read-only.
   */
  formatCode(): void {
    if (!this.view || this.effectiveReadOnly() || this.isPythonLanguage()) {
      return;
    }
    const view = this.view;
    // Select the whole document, re-indent it via the language's indent
    // service, then collapse the cursor back to the start.
    view.dispatch({ selection: { anchor: 0, head: view.state.doc.length } });
    indentSelection(view);
    view.dispatch({ selection: { anchor: 0 } });
    view.scrollDOM.scrollTop = 0;
  }

  private isPythonLanguage(): boolean {
    const key = (this.effectiveLanguage() || '').toLowerCase();
    return key === 'python' || key === 'python3' || key === 'text/x-python';
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
    if (this.view) {
      this.view.dispatch({
        effects: this.readOnlyConf.reconfigure(this.readOnlyExtension()),
      });
    }
  }

  // ----- view construction -----
  private createView(): void {
    if (this.view || !this.host) {
      return;
    }
    const state = EditorState.create({
      doc: this.value,
      extensions: [
        this.baseExtensions(),
        this.languageConf.of(this.languageExtension()),
        this.readOnlyConf.of(this.readOnlyExtension()),
        this.themeConf.of(this.themeExtension()),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const doc = update.state.doc.toString();
            this.value = doc;
            this.onChange(doc);
            this.codeChange.emit(doc);
          }
          if (update.focusChanged && !update.view.hasFocus) {
            this.onTouched();
          }
        }),
      ],
    });

    this.view = new EditorView({ state, parent: this.host.nativeElement });
  }

  private baseExtensions(): Extension {
    const exts: Extension[] = [
      history(),
      drawSelection(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      bracketMatching(),
      indentUnit.of('    '), // 4 spaces, matching CM5 indentUnit/tabSize
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      EditorView.lineWrapping,
      keymap.of([
        {
          key: 'Enter',
          run: (view) => {
            this.enterPressed.emit(this.cm5DocShim(view));
            return false; // let CM6 perform its own newline-and-indent
          },
        },
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
      ]),
    ];

    if (this.effectiveAutoCloseBrackets()) {
      exts.push(closeBrackets());
    }

    // Show line numbers unless the options object explicitly disables them
    // (CM5 `lineNumbers: false`, used by the read-only output panel).
    if (this.effectiveShowLineNumbers()) {
      if (this.effectiveNumberedLines()) {
        exts.push(lineNumbers({ formatNumber: (n) => `${n}.` }));
      } else {
        exts.push(lineNumbers());
      }
    }

    if (this.effectiveBlockPaste()) {
      exts.push(
        EditorView.domEventHandlers({
          paste: (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.pasteBlocked.emit();
            return true;
          },
        })
      );
    }

    return exts;
  }

  /** Minimal CM5-style facade over the CM6 document for legacy Enter handlers. */
  private cm5DocShim(view: EditorView): {
    lineCount: () => number;
    getLine: (i: number) => string;
  } {
    const doc = view.state.doc;
    return {
      lineCount: () => doc.lines,
      // CM5 getLine is 0-indexed; CM6 doc.line is 1-indexed.
      getLine: (i: number) => (i >= 0 && i < doc.lines ? doc.line(i + 1).text : ''),
    };
  }

  private languageExtension(): Extension {
    const key = (this.effectiveLanguage() || '').toLowerCase();
    switch (key) {
      case 'cpp':
      case 'c':
      case 'csharp': // approximated with the C-family grammar
      case 'text/x-c++src':
      case 'text/x-csrc':
      case 'text/x-csharp':
        return cpp();
      case 'java':
      case 'text/x-java':
        return java();
      case 'python':
      case 'python3':
      case 'text/x-python':
        return python();
      case 'sql':
        return sql();
      case 'javascript':
      case 'js':
        return javascript();
      default:
        return javascript();
    }
  }

  private readOnlyExtension(): Extension {
    const ro = this.effectiveReadOnly();
    return [EditorState.readOnly.of(ro), EditorView.editable.of(!ro)];
  }

  private themeExtension(): Extension {
    return this.effectiveTheme() === 'monokai' ? monokai : [];
  }
}
